import React, { useMemo } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { getSpecimenFeatures, type FeatureMap } from './specimenFeatures';
import { getTaxon } from './taxonomyDatabase';
import { traverseDichotomousKey, type DichotomousKey } from './dichotomousKey';

interface DichotomousKeyViewerProps {
  keyData: DichotomousKey;
  specimenIds: string[];
  observations: FeatureMap;
  onObserve: (featureId: string, value: string | number | boolean) => void;
  onComplete: (specimenId: string) => void;
}

const DichotomousKeyViewer: React.FC<DichotomousKeyViewerProps> = ({ keyData, specimenIds, observations, onObserve, onComplete }) => {
  const result = useMemo(() => traverseDichotomousKey(keyData, observations), [keyData, observations]);
  const node = result.currentNode;
  const values = node ? Array.from(new Set(specimenIds.map((id) => getSpecimenFeatures(id).features.find((feature) => feature.id === node.condition.featureId)?.value).filter((value): value is string | number | boolean => value !== undefined))) : [];
  const pathDescription = result.path.map((id) => {
    const pathNode = keyData.nodes[id];
    return pathNode?.type === 'couplet' ? pathNode.statementYes : id;
  }).join(' → ');
  const alternatives = values.filter((value) => value !== node?.condition.value);
  return <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
    <Typography variant="subtitle1" fontWeight={800}>{keyData.title}</Typography>
    {node && <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">Inspect the feature: {node.condition.featureId.replaceAll(/([A-Z])/g, ' $1').toLowerCase()}.</Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Button variant="outlined" onClick={() => onObserve(node.condition.featureId, node.condition.value)}>{node.statementYes}</Button>
        {alternatives.length ? alternatives.map((value) => <Button key={String(value)} variant="outlined" onClick={() => onObserve(node.condition.featureId, value)}>{node.statementNo}: {String(value).replaceAll('-', ' ')}</Button>) : <Button variant="outlined" onClick={() => onObserve(node.condition.featureId, `not-${String(node.condition.value)}`)}>{node.statementNo}</Button>}
      </Stack>
    </Box>}
    {result.status === 'identified' && result.specimenId && <Alert severity="info" sx={{ mt: 1 }} action={<Button color="inherit" size="small" onClick={() => onComplete(result.specimenId!)}>Use key result</Button>}>Key result: <i>{getTaxon(result.specimenId).scientificName}</i>. Verify it against the specimen features before submitting.</Alert>}
    {result.status === 'invalid' && <Alert severity="error" sx={{ mt: 1 }}>{result.message}</Alert>}
    {result.status === 'needs-observation' && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{result.message} Key path: {pathDescription}</Typography>}
  </Paper>;
};

export default DichotomousKeyViewer;
